import React, { useState } from "react";
import { Formik } from "formik";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import LinearProgress from "@mui/material/LinearProgress";
import { useQuery } from "@apollo/client/react";

import { fullWidthDialog } from "../../../../constants/dialog";
import { CaptureProvider } from "../../../../contexts/capture";
import { fetchAssetValues } from "../../../../utils/asset";
import { AssetsProvider } from "../../../../contexts/assets";
import CollectionProvider from "../../../../views/capture/CollectionProvider";
import KitDetailsModal from "../../../../views/shopping/Orders/KitDetailsModal";
import FormPage from "./FormPage";
import { ASSET_QUERY } from "../../../../constants/graphql";
import useAuth from "../../../../hooks/useAuth";

export default function AssetModal({
  id,
  reFetch,
  onClose,
  open,
  showOnlySave = false,
  hideAddNew = false,
  autoCloseOnSave = false,
  onAfterSave,
  variant = "wide",
}) {
  const { user } = useAuth();
  const { settings } = user.company;

  // Track the active asset locally so duplicating can re-point the modal to the
  // newly created copy (mirrors the iPad behavior), instead of leaving it on the
  // original (which still shows the original's barcode and confuses users).
  const [activeId, setActiveId] = useState(id);

  const { data: { asset } = {}, loading } = useQuery(ASSET_QUERY, {
    variables: { id: activeId },
    fetchPolicy: "network-only",
  });

  const warehouseDropdownEnabled = settings.warehouse_id_dropdown_menu;

  if (loading) return <LinearProgress sx={{ height: "1px" }} />;

  const isWide = variant === "wide";

  return (
    <>
      {asset?.type === "Asset" && (
        <Dialog 
          open={open} 
          fullWidth 
          maxWidth={isWide ? false : "xs"} 
          onClose={onClose}
          sx={isWide ? {
            "& .MuiDialog-paper": {
              maxWidth: "1000px",
              width: "100%",
            },
          } : fullWidthDialog}
        >
          <DialogTitle
            sx={{ background: "#f4f6f8", display: "flex", justifyContent: "space-between" }}
          >
            <Box width="48px" />
            <Typography fontSize={24} fontWeight={500} color="textPrimary">
              Item Details
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ background: "#f4f6f8" }}>
            <Box>
              <CaptureProvider>
                <CollectionProvider skipQuery>
                  <AssetsProvider assetId={activeId}>
                    <Formik
                      key={asset?.id}
                      enableReinitialize
                      initialValues={fetchAssetValues(asset)}
                      onSubmit={() => {}}
                    >
                      <FormPage
                        type="orders"
                        displayClient={!warehouseDropdownEnabled}
                        displayProject={true}
                        displayWarehouse={warehouseDropdownEnabled}
                        currentAsset={asset}
                        onCloseModal={onClose}
                        handeRefetch={reFetch}
                        showOnlySave={showOnlySave}
                        hideAddNew={hideAddNew}
                        autoCloseOnSave={autoCloseOnSave}
                        onAfterSave={onAfterSave}
                        openDuplicatedAsset={(newAsset) => setActiveId(newAsset.id)}
                        fullWidth={!isWide}
                      />
                    </Formik>
                  </AssetsProvider>
                </CollectionProvider>
              </CaptureProvider>
            </Box>
          </DialogContent>
        </Dialog>
      )}
      {asset?.type === "Kit" && <KitDetailsModal asset={asset} onClose={onClose} />}
    </>
  );
}
